import update from 'immutability-helper';
import { createReducer } from '../modules/helpers';
import { ActionTypes } from '../constants/index';

const initState = {
};

export const editorReducer = createReducer(initState, {

  [ActionTypes.EDITOR_GET_REQUEST](state, { payload }) {
    const { variantUrlsafe } = payload;
    return update(state, {
      [variantUrlsafe]: {
        $set: { status: 'loading' },
      },
    });
  },
  [ActionTypes.EDITOR_GET_SUCCESS](state, { payload }) {
    const { urlsafe, blocks } = payload;
    const syncPayload = {
      ...payload,
      blocks: blocks.map(b => ({
        ...b,
        localedits: 0,
        synctill: 0,
      })),
    };
    return update(state, {
      [urlsafe]: { $set: { ...syncPayload } },
    });
  },
  [ActionTypes.EDITOR_GET_FAILURE](state) {
    return state;
  },

  [ActionTypes.EDITOR_BLOCK_ADD_REQUEST](state, { payload }) {
    const {
      variantUrlsafe,
      insertIndex,
      name,
      content,
      tempBlockUrlsafe,
    } = payload;

    const numBlocks = state[variantUrlsafe].blocks.length;
    if (insertIndex > numBlocks) {
      console.assert(true, 'out of bound access');
      return state;
    }
    const newBlock = {
      name,
      content,
      urlsafe: tempBlockUrlsafe,
      status: 'loading',
      localedits: 0,
      synctill: 0,
    };
    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          $splice: [[insertIndex, 0, newBlock]],
        },
      },
    });
  },
  [ActionTypes.EDITOR_BLOCK_ADD_SUCCESS](state, { payload }) {
    const { variantUrlsafe, block, tempBlockUrlsafe } = payload;
    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === tempBlockUrlsafe);
    console.assert(foundIndex !== -1, 'Internal error - block to be updated not found');

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          [foundIndex]: {
            name: { $set: block.name },
            content: { $set: block.content },
            urlsafe: { $set: block.urlsafe },
            status: { $set: 'idle' },
            updated: { $set: block.updated },
            synctill: { $set: 0 },
            localedits: { $set: 0 },
          },
        },
      },
    });
  },

  [ActionTypes.EDITOR_BLOCKS_ADD_REQUEST](state, { payload }) {
    const {
      variantUrlsafe,
      insertIndex,
      blocks,
    } = payload;

    const numBlocks = state[variantUrlsafe].blocks.length;
    if (insertIndex > numBlocks) {
      console.assert(true, 'out of bound access');
      return state;
    }
    const newBlocks = [];

    blocks.forEach((block) => {
      const copyBlock = {};
      copyBlock.name = block.name;
      copyBlock.content = block.content;
      copyBlock.urlsafe = block.tempBlockUrlsafe;
      copyBlock.status = 'loading';
      copyBlock.localedits = 0;
      copyBlock.synctill = 0;
      newBlocks.push(copyBlock);
    });

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          $splice: [[insertIndex, 0, ...newBlocks]],
        },
      },
    });
  },

  [ActionTypes.EDITOR_BLOCKS_ADD_SUCCESS](state, { payload }) {
    const { variantUrlsafe, blocks } = payload;
    let newState = state;

    blocks.forEach(() => {
      let foundIndex = null;
      blocks.forEach((block2) => {
        foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === block2.tempBlockUrlsafe);
        if (foundIndex >= 0) {
          newState = update(newState, {
            [variantUrlsafe]: {
              blocks: {
                [foundIndex]: {
                  name: { $set: block2.name },
                  content: { $set: block2.content },
                  urlsafe: { $set: block2.urlsafe },
                  status: { $set: 'idle' },
                  synctill: { $set: 0 },
                },
              },
            },
          });
        }
      });
    });

    return newState;
  },

  [ActionTypes.EDITOR_BLOCK_DELETE_REQUEST](state, { payload }) {
    const { variantUrlsafe, blockUrlsafe } = payload;
    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === blockUrlsafe);
    console.assert(foundIndex !== -1, 'Block to be deleted is not accessible');

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          $splice: [[foundIndex, 1]],
        },
      },
    });
  },
  [ActionTypes.EDITOR_BLOCK_DELETE_SUCCESS](state) {
    return state;
  },

  [ActionTypes.EDITOR_ELEMENT_DELETE_REQUEST](state, { payload }) {
    const { variantUrlsafe, blockUrlsafe, address } = payload;
    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === blockUrlsafe);
    console.assert(foundIndex !== -1, 'Block to be deleted is not accessible');

    const { content, localedits } = state[variantUrlsafe].blocks[foundIndex];
    const createHierDelete = (depth, contentPointer) => {
      if (depth > address.length) {
        console.assert(true, 'address too long');
        return state;
      }

      if (depth === address.length - 1) {
        const offset = address[depth];
        if (Array.isArray(contentPointer.props.children)) {
          return {
            props: {
              children: {
                $splice: [[offset, 1]],
              },
            },
          };
        }
        return {
          props: {
            $unset: ['children'],
          },
        };
      }

      if (Array.isArray(contentPointer.props.children)) {
        const offset = address[depth];
        return {
          props: {
            children: {
              [offset]: createHierDelete(depth + 1, contentPointer.props.children[offset]),
            },
          },
        };
      }
      return {
        props: {
          children: createHierDelete(depth + 1, contentPointer.props.children),
        },
      };
    };
    const hierDelete = createHierDelete(1, content);

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          [foundIndex]: {
            content: hierDelete,
            localedits: { $set: (localedits + 1) },
          },
        },
      },
    });
  },
  [ActionTypes.EDITOR_ELEMENT_DELETE_SUCCESS](state) {
    return state;
  },

  [ActionTypes.EDITOR_BLOCK_REORDER_REQUEST](state, { payload }) {
    const { variantUrlsafe, previousIndex, nextIndex } = payload;
    if (previousIndex === nextIndex) {
      return state;
    }

    const movedBlock = state[variantUrlsafe].blocks[previousIndex];
    const intermediateState = update(state, {
      [variantUrlsafe]: {
        blocks: {
          $splice: [[previousIndex, 1]],
        },
      },
    });
    return update(intermediateState, {
      [variantUrlsafe]: {
        blocks: {
          $splice: [[nextIndex, 0, movedBlock]],
        },
      },
    });
  },
  [ActionTypes.EDITOR_BLOCK_REORDER_SUCCESS](state) {
    return state;
  },

  [ActionTypes.EDITOR_BLOCK_UPDATE_REQUEST](state, { payload }) {
    const { variantUrlsafe, blockUrlsafe, address, updatedProps } = payload;

    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === blockUrlsafe);
    console.assert(foundIndex !== -1, 'Cannot find block to update');
    const { content, localedits } = state[variantUrlsafe].blocks[foundIndex];

    const assignArgs = { props: {} };
    for (const [key, value] of Object.entries(updatedProps)) {
      assignArgs.props[key] = { $set: value };
    }

    const createHierUpdate = (depth, contentPointer) => {
      if (depth > address.length) {
        console.assert(true, 'address too long');
        return state;
      }

      if (depth === address.length) {
        return assignArgs;
      }

      if (Array.isArray(contentPointer.props.children)) {
        const offset = address[depth];
        return {
          props: {
            children: {
              [offset]: createHierUpdate(depth + 1, contentPointer.props.children[offset]),
            },
          },
        };
      }
      return {
        props: {
          children: createHierUpdate(depth + 1, contentPointer.props.children),
        },
      };
    };
    const hierUpdate = createHierUpdate(1, content);

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          [foundIndex]: {
            content: hierUpdate,
            localedits: { $set: (localedits + 1) },
          },
        },
      },
    });
  },

  [ActionTypes.EDITOR_COLOR_UPDATE_REQUEST](state, { payload }) {
    const { variantUrlsafe, color } = payload;
    return update(state, {
      [variantUrlsafe]: {
        color: { $set: color },
      },
    });
  },

  [ActionTypes.EDITOR_COLOR_UPDATE_SUCCESS](state, { payload }) {
    const { variantUrlsafe, color, updated } = payload;
    return update(state, {
      [variantUrlsafe]: {
        color: { $set: color },
        updated: { $set: updated },
      },
    });
  },

  [ActionTypes.EDITOR_FONT_UPDATE_REQUEST](state, { payload }) {
    const { variantUrlsafe, font } = payload;
    return update(state, {
      [variantUrlsafe]: {
        font: { $set: font },
      },
    });
  },
  [ActionTypes.EDITOR_FONT_UPDATE_SUCCESS](state, { payload }) {
    const { variantUrlsafe, font, updated } = payload;
    return update(state, {
      [variantUrlsafe]: {
        font: { $set: font },
        updated: { $set: updated },
      },
    });
  },

  [ActionTypes.EDITOR_SEO_UPDATE_REQUEST](state, { payload }) {
    const { variantUrlsafe, key, value } = payload;
    return update(state, {
      [variantUrlsafe]: {
        seo: {
          [key]: { $set: value },
        },
      },
    });
  },
  [ActionTypes.EDITOR_SEO_UPDATE_SUCCESS](state, { payload }) {
    const { variantUrlsafe, seo, updated } = payload;
    return update(state, {
      [variantUrlsafe]: {
        seo: { $set: seo },
        updated: { $set: updated },
      },
    });
  },

  [ActionTypes.EDITOR_GID_UPDATE_REQUEST](state, { payload }) {
    const { variantUrlsafe, key, val } = payload;
    return update(state, {
      [variantUrlsafe]: {
        analytics: {
          [key]: { $set: val },
        },
      },
    });
  },
  [ActionTypes.EDITOR_GID_UPDATE_SUCCESS](state, { payload }) {
    const { variantUrlsafe, analytics, updated } = payload;
    return update(state, {
      [variantUrlsafe]: {
        analytics: { $set: analytics },
        updated: { $set: updated },
      },
    });
  },

  [ActionTypes.EDITOR_BLOCK_RENAME_REQUEST](state, { payload }) {
    const { variantUrlsafe, blockUrlsafe, name } = payload;
    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === blockUrlsafe);
    console.assert(foundIndex !== -1, 'Block to be renamed is not accessible');
    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          [foundIndex]: {
            name: { $set: name },
          },
        },
      },
    });
  },

  [ActionTypes.EDITOR_BLOCK_SYNC_REQUEST](state) {
    return state;
  },
  [ActionTypes.EDITOR_BLOCK_SYNC_SUCCESS](state, { payload }) {
    const { variantUrlsafe, blockUrlsafe, synctill, updated } = payload;
    const foundIndex = state[variantUrlsafe].blocks.findIndex(x => x.urlsafe === blockUrlsafe);
    console.assert(foundIndex !== -1, 'Block to be synced is not accessible');

    return update(state, {
      [variantUrlsafe]: {
        blocks: {
          [foundIndex]: {
            synctill: { $set: synctill },
            updated: { $set: updated },
          },
        },
      },
    });
  },

  [ActionTypes.EDITOR_PUBLISH_REQUEST](state, { payload }) {
    const { variantUrlsafe } = payload;
    return update(state, {
      [variantUrlsafe]: {
        status: { $set: "publishing" },
      },
    });
  },
  [ActionTypes.EDITOR_PUBLISH_SUCCESS](state, { payload }) {
    const { variantUrlsafe, published } = payload;
    return update(state, {
      [variantUrlsafe]: {
        published: { $set: published },
        status: { $set: "published" },
      },
    });
  },

  [ActionTypes.EDITOR_BLOCK_RENAME_SUCCESS](state) {
    return state;
  },

});



// WEBPACK FOOTER //
// ./scripts/reducers/editor.js